/**
 * Notification Polling Hook
 * Monitors Firebase for new notifications and displays them as browser notifications
 * Works with Firebase real-time listeners to show notifications when app is in background
 */

import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ref, onValue, query, orderByChild, limitToLast } from 'firebase/database';
import { database } from '../lib/firebase';

const SHOWN_NOTIFICATIONS_KEY = 'notification_shown_ids';

interface UserNotification {
  id: string;
  title: string;
  content: string;
  category: string;
  actionUrl?: string;
  sentAt: string;
  sentBy: string;
  read: boolean;
  seen: boolean;
}

export function useNotificationPolling() {
  const { user } = useAuth();
  const [isListening, setIsListening] = useState(false);
  const [lastNotification, setLastNotification] = useState<Date | null>(null);
  const shownIdsRef = useRef<Set<string>>(new Set());

  // Load previously shown IDs from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(SHOWN_NOTIFICATIONS_KEY);
    if (stored) {
      try {
        const ids = JSON.parse(stored) as string[];
        shownIdsRef.current = new Set(ids);
      } catch (error) {
        console.error('❌ Error loading shown notification IDs:', error);
      }
    }
  }, []);

  const displayBrowserNotification = (notification: UserNotification) => {
    console.log('📬 [useNotificationPolling] Attempting to display browser notification:', notification.id, notification.title);
    
    // Skip if already shown
    if (shownIdsRef.current.has(notification.id)) {
      console.log('📬 [useNotificationPolling] Notification already shown, skipping:', notification.id);
      return;
    }

    try {
      // Check if Notification constructor is available
      if (typeof Notification === 'undefined') {
        console.warn('⚠️ [useNotificationPolling] Notification API not available');
        return;
      }

      console.log('📬 [useNotificationPolling] Notification permission:', Notification.permission);
      
      if (Notification.permission !== 'granted') {
        console.warn('⚠️ [useNotificationPolling] Notification permission not granted');
        return;
      }

      console.log('📬 [useNotificationPolling] Creating browser notification...');
      
      // Enhanced notification options for better native appearance
      const notificationOptions: NotificationOptions = {
        body: notification.content || '',
        icon: '/vite.svg', // 192x192 recommended - replace with custom icon
        badge: '/vite.svg', // 96x96 monochrome badge - replace with custom badge
        tag: notification.id, // Prevents duplicate notifications
        requireInteraction: false,
        silent: false,
        timestamp: new Date(notification.sentAt).getTime(),
        vibrate: [200, 100, 200], // Vibration pattern for mobile devices
        dir: 'auto', // Text direction
        lang: 'en', // Language
        renotify: false, // Don't renotify for same tag
      };

      const browserNotification = new Notification(notification.title || 'New notification', notificationOptions);

      // Handle notification click
      browserNotification.onclick = () => {
        console.log('📬 [useNotificationPolling] Notification clicked');
        window.focus();
        if (notification.actionUrl) {
          // Navigate to the action URL
          window.location.href = notification.actionUrl;
        }
        browserNotification.close();
      };

      // Handle notification close
      browserNotification.onclose = () => {
        console.log('📬 [useNotificationPolling] Notification closed');
      };

      // Mark as shown
      shownIdsRef.current.add(notification.id);
      localStorage.setItem(
        SHOWN_NOTIFICATIONS_KEY,
        JSON.stringify(Array.from(shownIdsRef.current))
      );

      setLastNotification(new Date());
      console.log('✅ [useNotificationPolling] Browser notification displayed successfully:', notification.title);
    } catch (error) {
      console.error('❌ [useNotificationPolling] Error displaying notification:', error);
      // Still mark as shown to prevent retry loops
      shownIdsRef.current.add(notification.id);
      localStorage.setItem(
        SHOWN_NOTIFICATIONS_KEY,
        JSON.stringify(Array.from(shownIdsRef.current))
      );
    }
  };

  useEffect(() => {
    console.log('📡 [useNotificationPolling] Effect triggered');
    console.log('📡 [useNotificationPolling] User:', user?.uid, user?.email);
    
    try {
      // Check if we're in a browser environment
      if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        console.log('📡 [useNotificationPolling] Not in browser environment, skipping notification listener');
        setIsListening(false);
        return;
      }

      console.log('📡 [useNotificationPolling] Notification API available:', typeof Notification !== 'undefined');
      console.log('📡 [useNotificationPolling] Current permission:', typeof Notification !== 'undefined' ? Notification.permission : 'N/A');

      // Don't start listening if no user or no permission
      if (!user?.uid) {
        console.log('📡 [useNotificationPolling] No user, skipping listener');
        setIsListening(false);
        return;
      }
      
      if (typeof Notification === 'undefined') {
        console.log('📡 [useNotificationPolling] Notification API not available, skipping listener');
        setIsListening(false);
        return;
      }
      
      if (Notification.permission !== 'granted') {
        console.log('📡 [useNotificationPolling] Permission not granted, skipping listener. Current permission:', Notification.permission);
        setIsListening(false);
        return;
      }

      console.log('📡 [useNotificationPolling] Setting up real-time notification listener for user:', user.uid);
      setIsListening(true);

      // Set up Firebase real-time listener for notifications
      const notificationsPath = `users/${user.uid}/notifications`;
      console.log('📡 [useNotificationPolling] Listening to path:', notificationsPath);
      
      const notificationsRef = ref(database, notificationsPath);
      const notificationsQuery = query(
        notificationsRef,
        orderByChild('sentAt'),
        limitToLast(10) // Listen to last 10 notifications
      );

      const unsubscribe = onValue(notificationsQuery, (snapshot) => {
        console.log('📡 [useNotificationPolling] Snapshot received');
        console.log('📡 [useNotificationPolling] Snapshot exists:', snapshot.exists());
        
        if (snapshot.exists()) {
          const data = snapshot.val();
          const notifications: UserNotification[] = Object.values(data);
          console.log('📡 [useNotificationPolling] Found', notifications.length, 'notifications');
          console.log('📡 [useNotificationPolling] Notifications:', notifications.map(n => ({
            id: n.id,
            title: n.title,
            seen: n.seen,
            alreadyShown: shownIdsRef.current.has(n.id)
          })));

          // Display browser notifications for new unseen ones
          notifications.forEach((notification) => {
            console.log('📡 [useNotificationPolling] Checking notification:', notification.id, 'seen:', notification.seen, 'alreadyShown:', shownIdsRef.current.has(notification.id));
            // Only show if not seen and not already shown
            if (!notification.seen && !shownIdsRef.current.has(notification.id)) {
              console.log('📡 [useNotificationPolling] Displaying notification:', notification.id);
              displayBrowserNotification(notification);
            }
          });
        } else {
          console.log('📡 [useNotificationPolling] No notifications in Firebase');
        }
      });

      console.log('📡 [useNotificationPolling] Listener attached successfully');

      // Cleanup on unmount
      return () => {
        unsubscribe();
        console.log('📡 [useNotificationPolling] Stopped notification listener');
        setIsListening(false);
      };
    } catch (error) {
      console.error('❌ [useNotificationPolling] Error setting up notification listener:', error);
      setIsListening(false);
      return;
    }
  }, [user?.uid]);

  return {
    isListening,
    lastNotification,
  };
}

